import {TargetBlank} from '@wandb/weave/common/util/links';
import {Icon} from '@wandb/weave/components/Icon';
import React, {FC, Fragment, memo, useContext} from 'react';

import {createDismissibleBanner} from '../../../pages/Billing/AccountSettings/WarningBanner/DismissibleBanner';
import {Analytics} from '../../../services/analytics';
import {TeamOrgQueryResult, useCurrentTeamOrg} from '../../../util/nudgeBars';
import {
  STALE_DATA_WARNING_MESSAGE,
  STORAGE_LIMIT_GB,
} from '../../../util/storage';
import {
  accountSettingsBillingTab,
  accountSettingsUsageTab,
} from '../../../util/urls';
import {
  Banner,
  BannerBoldText,
  BannerFlexWrapper,
  BannerTextWrapper,
} from '../../Banner';
import {Tailwind} from '../../Tailwind';
import {GlobalBannerContext} from '..';

export type ResourceSubscriptionLimitExceededWarningBannerProps = {
  organizationId: string;
  organizationName: string;
  isBillingAdmin: boolean;
  billingAdminEmail?: string;
  resource: 'storage' | 'artifact storage';
};

export const ResourceSubscriptionLimitExceededWarningBannerGlobalList: FC =
  () => {
    const currentTeamOrg: TeamOrgQueryResult | null = useCurrentTeamOrg();

    const {states} = useContext(GlobalBannerContext);
    if (states == null || states.length === 0) {
      return null;
    }

    const organizationStatesToShow = currentTeamOrg
      ? states?.filter(
          state =>
            state.organization?.name ===
            currentTeamOrg?.entity?.organization?.name
        )
      : states;

    if (organizationStatesToShow == null) {
      return null;
    }

    return (
      <Tailwind>
        <div className="mb-8 space-y-8">
          {organizationStatesToShow.map(state => {
            const storageSubscriptionLimitExceededWarningBannerProps =
              state.storageAndEnforcementBannerFlags
                .storageSubscriptionLimitExceededWarningBannerProps;
            const artifactStorageSubscriptionLimitExceededWarningBannerProps =
              state.storageAndEnforcementBannerFlags
                .artifactStorageSubscriptionLimitExceededWarningBannerProps;

            return (
              <Fragment key={state.organization?.id}>
                {storageSubscriptionLimitExceededWarningBannerProps && (
                  <ResourceSubscriptionLimitExceededWarningBanner
                    {...storageSubscriptionLimitExceededWarningBannerProps}
                  />
                )}
                {artifactStorageSubscriptionLimitExceededWarningBannerProps && (
                  <ResourceSubscriptionLimitExceededWarningBanner
                    {...artifactStorageSubscriptionLimitExceededWarningBannerProps}
                  />
                )}
              </Fragment>
            );
          })}
        </div>
      </Tailwind>
    );
  };

const DismissibleResourceSubscriptionLimitExceededWarningBannerForNonBillingAdmin: FC<
  ResourceSubscriptionLimitExceededWarningBannerProps
> = ({
  organizationId,
  organizationName,
  isBillingAdmin,
  billingAdminEmail,
  resource,
}) => {
  const DismissibleBanner =
    createDismissibleBanner<ResourceSubscriptionLimitExceededWarningBannerProps>(
      {
        id: () => {
          let resourceName: string = resource;
          if (resource === 'artifact storage') {
            resourceName = 'artifacts';
          }
          return organizationId + ':' + resourceName + '-exceeded';
        },
        Component: ({onDismiss}) => (
          <Tailwind>
            <Banner
              variant={'error'}
              eventData={{
                location: `${resource} subscription limit exceeded banner`,
                organizationId,
                resource,
              }}>
              <BannerFlexWrapper>
                <div className="text-xs">
                  <Icon name="warning" />
                </div>
                <BannerTextWrapper>
                  <span>
                    <BannerBoldText>
                      You have exceeded the free {resource} limit of{' '}
                      {STORAGE_LIMIT_GB} GB.{' '}
                    </BannerBoldText>
                    Contact your admin
                    {billingAdminEmail != null
                      ? ` at ${billingAdminEmail} `
                      : ' '}
                    to manage usage or add storage to your plan.{' '}
                    {STALE_DATA_WARNING_MESSAGE}.
                  </span>
                </BannerTextWrapper>
              </BannerFlexWrapper>
              <button
                className="flex items-center border-none bg-transparent font-semibold text-red-500 hover:cursor-pointer hover:text-red-550"
                onClick={() => {
                  Analytics.track('Dismiss button clicked', {
                    location: `${resource} subscription limit exceeded banner`,
                    organizationId,
                    resource,
                  });
                  onDismiss();
                }}>
                Dismiss
              </button>
            </Banner>
          </Tailwind>
        ),
      }
    );

  return (
    <DismissibleBanner
      {...{
        organizationId,
        organizationName,
        isBillingAdmin,
        resource,
      }}
    />
  );
};

const ResourceSubscriptionLimitExceededWarningBannerComp: FC<
  ResourceSubscriptionLimitExceededWarningBannerProps
> = ({
  organizationId,
  organizationName,
  isBillingAdmin,
  billingAdminEmail,
  resource,
}) => {
  if (!isBillingAdmin) {
    return (
      <DismissibleResourceSubscriptionLimitExceededWarningBannerForNonBillingAdmin
        {...{
          organizationId,
          organizationName,
          isBillingAdmin,
          billingAdminEmail,
          resource,
        }}
      />
    );
  }

  return (
    <Tailwind>
      <Banner
        variant={'error'}
        eventData={{
          location: `${resource} subscription limit exceeded banner`,
          organizationId,
          resource,
        }}>
        <BannerFlexWrapper>
          <div className="text-xs">
            <Icon name="warning" />
          </div>
          <BannerTextWrapper>
            <span>
              <BannerBoldText>
                You have exceeded the free {resource} limit of{' '}
                {STORAGE_LIMIT_GB} GB.{' '}
              </BannerBoldText>
              <TargetBlank
                className="font-extrabold text-red-500 hover:text-red-550"
                href={
                  accountSettingsBillingTab(organizationName) +
                  '?openBuyStorageModal=true'
                }>
                Upgrade to pay-as-you-go
              </TargetBlank>{' '}
              or{' '}
              <TargetBlank
                className="mr-8 font-extrabold text-red-500 hover:text-red-550"
                href={accountSettingsUsageTab(organizationName)}>
                manage usage
              </TargetBlank>
              to continue using wandb without disruption.{' '}
              {STALE_DATA_WARNING_MESSAGE}.
            </span>
          </BannerTextWrapper>
        </BannerFlexWrapper>
        <div className="flex items-center">
          <TargetBlank
            className="mr-8 font-extrabold text-red-500 hover:text-red-550"
            href={accountSettingsUsageTab(organizationName)}>
            Manage usage
          </TargetBlank>
          <TargetBlank
            className="font-extrabold text-red-500 hover:text-red-550"
            href={
              accountSettingsBillingTab(organizationName) +
              '?openBuyStorageModal=true'
            }>
            Add storage
          </TargetBlank>
        </div>
      </Banner>
    </Tailwind>
  );
};

export const ResourceSubscriptionLimitExceededWarningBanner = memo(
  ResourceSubscriptionLimitExceededWarningBannerComp
);
